(() => {
  'use strict';

  const QUICK_SHOP_FORM = 'form[id*="main-qs"]';
  const PRODUCT_FORM = 'form[data-type="add-to-cart-form"]';
  const MAIN_PRODUCT_FORM = `${PRODUCT_FORM}:not([id*="main-qs"])`;
  const PROTECTED_FORM = `${QUICK_SHOP_FORM}, ${PRODUCT_FORM}`;
  const STICKY_ATC = '[data-sticky-addtocart]';
  const OPTION_GROUP = '[data-swatch-option]';
  const OPTION_ITEM = '[data-swatch-item]';
  const AVAILABLE_OPTION_ITEM =
    `${OPTION_ITEM}:not([disabled]):not(.is--soldout):not(.is--unavailable)`;
  const ERROR_CLASS = 'evano-variant-choice-error';
  const UNCONFIRMED_CLASS = 'is--evano-size-unconfirmed';
  const SIZE_PROMPT = 'Selecciona una talla';
  const confirmedSizesByForm = new WeakMap();
  const explicitVariantInitializedForms = new WeakSet();
  const explicitSizeUrlClearedForms = new WeakSet();

  const SIZE_OPTION_PATTERN = /(^|\s)(talla|size)(\s|$)/i;
  const initialVariantId = (() => {
    const navigationUrl = window.performance
      ?.getEntriesByType?.('navigation')
      ?.[0]?.name;

    if (!navigationUrl) return null;

    return new URL(navigationUrl).searchParams.get('variant');
  })();

  const isSizeGroup = (group) => {
    if (!group) return false;

    const labelId = group.getAttribute('aria-labelledby');
    const label = labelId ? document.getElementById(labelId)?.textContent : '';
    const optionName = `${group.className} ${label || ''}`
      .replace(/[_-]+/g, ' ')
      .trim();

    return (
      SIZE_OPTION_PATTERN.test(optionName) &&
      group.querySelectorAll(OPTION_ITEM).length > 1
    );
  };

  const sizeGroups = (form) =>
    Array.from(form.querySelectorAll(OPTION_GROUP)).filter(isSizeGroup);

  const requiresExplicitSize = (form) =>
    form?.dataset.evanoRequireExplicitSize === 'true' &&
    !form.matches(QUICK_SHOP_FORM);

  const groupKey = (group) =>
    group.dataset.id || group.getAttribute('aria-labelledby') || group.className;

  const selectedSizeValue = (group) =>
    group.querySelector(`${OPTION_ITEM}.is--selected`)?.dataset.value || null;

  const confirmedSizeValue = (form, group) =>
    confirmedSizesByForm.get(form)?.get(groupKey(group)) || null;

  const rememberConfirmedSize = (form, group, value) => {
    if (!value) return;

    let confirmedSizes = confirmedSizesByForm.get(form);
    if (!confirmedSizes) {
      confirmedSizes = new Map();
      confirmedSizesByForm.set(form, confirmedSizes);
    }

    confirmedSizes.set(groupKey(group), value);
    group.dataset.evanoConfirmedValue = value;
    group.classList.remove(UNCONFIRMED_CLASS);
  };

  const presentConfirmedSize = (group, value) => {
    group.querySelectorAll(OPTION_ITEM).forEach((option) => {
      const selected = option.dataset.value === value;
      option.classList.toggle('is--selected', selected);
      option.setAttribute('aria-pressed', String(selected));
      if (option.getAttribute('role') === 'radio') {
        option.setAttribute('aria-checked', String(selected));
      }
    });
    group.querySelectorAll('[data-current-value]').forEach((currentValue) => {
      currentValue.textContent = value;
    });
  };

  const productVariants = (form) => {
    const source = form.closest('.t4s-product-form__variants')
      ?.querySelector('.pr_variants_json');
    if (!source?.textContent) return [];
    try {
      const variants = JSON.parse(source.textContent);
      return Array.isArray(variants) ? variants : [];
    } catch (error) {
      console.error('EVANO could not read product variants.', error);
      return [];
    }
  };

  const syncResolvedPurchaseSurfaces = (form, size, variant) => {
    const mainAdd = form.querySelector('[data-atc-form]');
    const sticky = document.querySelector(STICKY_ATC);
    const stickyAdd = sticky?.querySelector('[data-action-atc]');
    const mainLabel = mainAdd?.querySelector('.t4s-btn-atc_text');
    const stickyLabel = stickyAdd?.querySelector('.t4s-btn-atc_text');
    const price = sticky?.querySelector('[data-sticky-price]')?.textContent.trim() || '';
    const context = sticky?.querySelector('.evano-sticky-purchase-context');

    if (mainLabel) mainLabel.textContent = variant.available ? 'Añadir al carrito' : 'Agotado';
    if (mainAdd) {
      mainAdd.setAttribute('aria-label', variant.available
        ? `Añadir talla ${size} al carrito`
        : `Talla ${size}, agotada`);
      mainAdd.dataset.evanoPurchaseState = variant.available ? 'ready' : 'sold-out';
    }
    if (stickyLabel) stickyLabel.textContent = variant.available ? 'Añadir al carrito' : 'Agotado';
    if (stickyAdd) {
      stickyAdd.dataset.variantId = variant.available ? String(variant.id) : '';
      stickyAdd.setAttribute('aria-label', variant.available
        ? `Añadir talla ${size} al carrito`
        : `Talla ${size}, agotada`);
      stickyAdd.dataset.evanoPurchaseState = variant.available ? 'ready' : 'sold-out';
    }
    if (context) context.textContent = `Talla ${size}${price ? ` · ${price}` : ''}`;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`
    );
  };

  const resolveExplicitVariant = (form, changedGroup, changedValue) => {
    if (!requiresExplicitSize(form)) return;

    const selections = [...form.querySelectorAll(OPTION_GROUP)].map((group) => {
      if (group === changedGroup) return changedValue;
      return group.querySelector(`${OPTION_ITEM}.is--selected`)?.dataset.value || null;
    });
    if (selections.some((value) => !value)) return;

    const variant = productVariants(form).find((candidate) =>
      candidate.options?.every((value, index) => value === selections[index])
    );
    const select = form.querySelector('select[name="id"]');
    if (!variant || !select) return;

    select.value = String(variant.id);
    syncResolvedPurchaseSurfaces(form, changedValue, variant);
    document.dispatchEvent(new CustomEvent('evano:explicit-variant-resolved', {
      detail: { form, variantId: String(variant.id) }
    }));
  };

  const forgetConfirmedSize = (form, group) => {
    confirmedSizesByForm.get(form)?.delete(groupKey(group));
    delete group.dataset.evanoConfirmedValue;
  };

  const clearUnconfirmedVariant = (form) => {
    if (!requiresExplicitSize(form)) return;

    const variantSelect = form.querySelector('select[name="id"]');
    if (variantSelect?.value) variantSelect.value = '';

    const stickyAdd = document.querySelector(`${STICKY_ATC} [data-action-atc]`);
    if (stickyAdd) stickyAdd.dataset.variantId = '';

    if (explicitSizeUrlClearedForms.has(form)) return;
    const url = new URL(window.location.href);
    if (url.searchParams.has('variant')) {
      url.searchParams.delete('variant');
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    }
    explicitSizeUrlClearedForms.add(form);
  };

  const resetSizeGroup = (form, group) => {
    group.classList.add(UNCONFIRMED_CLASS);
    group.querySelectorAll(OPTION_ITEM).forEach((option) => {
      option.classList.remove('is--selected');
      option.setAttribute('aria-pressed', 'false');
    });

    group.querySelectorAll('[data-current-value]').forEach((currentValue) => {
      if (currentValue.textContent !== SIZE_PROMPT) {
        currentValue.textContent = SIZE_PROMPT;
      }
    });
    clearUnconfirmedVariant(form);
  };

  const hasExplicitVariantInUrl = (form) => {
    if (!initialVariantId || form.matches(QUICK_SHOP_FORM) || requiresExplicitSize(form)) return false;

    const variantOption = Array.from(
      form.querySelectorAll('select[name="id"] option')
    ).find((option) => option.value === initialVariantId);

    return Boolean(variantOption?.selected && !variantOption.disabled);
  };

  const reconcileSizePresentation = (form) => {
    const initializeExplicitVariant =
      hasExplicitVariantInUrl(form) && !explicitVariantInitializedForms.has(form);

    sizeGroups(form).forEach((group) => {
      const selectedValue = selectedSizeValue(group);
      let confirmedValue = confirmedSizeValue(form, group);

      if (!confirmedValue && initializeExplicitVariant && selectedValue) {
        rememberConfirmedSize(form, group, selectedValue);
        confirmedValue = selectedValue;
      }

      if (confirmedValue && selectedValue === confirmedValue) {
        group.dataset.evanoConfirmedValue = confirmedValue;
        group.classList.remove(UNCONFIRMED_CLASS);
        group.querySelectorAll('[data-current-value]').forEach((currentValue) => {
          if (currentValue.textContent !== confirmedValue) {
            currentValue.textContent = confirmedValue;
          }
        });
        return;
      }

      forgetConfirmedSize(form, group);
      resetSizeGroup(form, group);
    });

    if (initializeExplicitVariant) explicitVariantInitializedForms.add(form);
  };

  const scheduleReconcile = (form) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => reconcileSizePresentation(form));
    });
  };

  const scheduleConfirmEffectiveSizes = (form) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        sizeGroups(form).forEach((group) => {
          rememberConfirmedSize(form, group, selectedSizeValue(group));
        });
        reconcileSizePresentation(form);
      });
    });
  };

  const clearError = (...containers) => {
    containers.filter(Boolean).forEach((container) => {
      container.querySelectorAll(`.${ERROR_CLASS}`).forEach((error) => error.remove());
    });
  };

  const focusMissingSize = (group) => {
    const dropdown = group.querySelector('[data-dropdown-open]');
    const option = group.querySelector(AVAILABLE_OPTION_ITEM);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    group.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'center'
    });
    window.requestAnimationFrame(() => {
      if (dropdown) {
        dropdown.focus({ preventScroll: true });
        dropdown.click();
        return;
      }

      option?.focus({ preventScroll: true });
    });
  };

  const focusStickyVariant = (sticky) => {
    const dropdown = sticky.querySelector('[data-dropdown-open]');

    if (!dropdown || (!dropdown.offsetWidth && !dropdown.offsetHeight)) return false;

    window.setTimeout(() => {
      const menuId = dropdown.getAttribute('data-id');
      const menu = menuId ? document.getElementById(menuId) : null;

      dropdown.focus({ preventScroll: true });
      if (!menu?.classList.contains('is--opened')) dropdown.click();
    }, 80);
    return true;
  };

  const hasVisibleStickyVariant = (sticky) => {
    const dropdown = sticky?.querySelector('[data-dropdown-open]');
    return Boolean(dropdown && (dropdown.offsetWidth || dropdown.offsetHeight));
  };

  const createSizeError = () => {
    const message = document.createElement('p');
    message.className = ERROR_CLASS;
    message.setAttribute('role', 'alert');
    message.setAttribute('aria-live', 'polite');
    message.textContent = 'Selecciona tu talla antes de añadir al carrito.';
    return message;
  };

  const promptInsideSticky = (sticky) => {
    clearError(sticky);

    const productPanel = sticky.querySelector('.t4s-sticky-atc__product');
    const buttonRow = sticky.querySelector('.t4s-sticky-atc__btns');
    if (!productPanel || !buttonRow || !focusStickyVariant(sticky)) return false;

    buttonRow.before(createSizeError());
    return true;
  };

  const scheduleNativeStickyPrompt = (sticky) => {
    if (promptInsideSticky(sticky)) return;

    const observer = new MutationObserver(() => {
      if (!promptInsideSticky(sticky)) return;
      observer.disconnect();
      window.clearTimeout(timeoutId);
    });
    const timeoutId = window.setTimeout(() => observer.disconnect(), 1500);

    observer.observe(sticky, {
      attributes: true,
      childList: true,
      subtree: true
    });
  };

  const requireExplicitOptions = (event, form, sticky = null) => {
    const missingGroup = sizeGroups(form).find((group) => {
      const confirmedValue = confirmedSizeValue(form, group);
      return !confirmedValue || confirmedValue !== selectedSizeValue(group);
    });

    if (!missingGroup) return false;

    event.preventDefault();
    event.stopImmediatePropagation();
    clearError(form, sticky);

    const message = createSizeError();

    if (sticky) {
      sticky.querySelector('.t4s-sticky-atc__btns')?.before(message);
      focusStickyVariant(sticky);
    } else {
      missingGroup.after(message);
      focusMissingSize(missingGroup);
    }

    return true;
  };

  const guardAdd = (event, form, sticky = null) => {
    if (!form) {
      event.preventDefault();
      event.stopImmediatePropagation();
      console.error('EVANO size guard could not resolve the product form.');
      return true;
    }

    try {
      return requireExplicitOptions(event, form, sticky);
    } catch (error) {
      event.preventDefault();
      event.stopImmediatePropagation();
      console.error('EVANO size guard prevented an unsafe add-to-cart.', error);
      return true;
    }
  };

  const mainProductForm = () =>
    document.querySelector(`${MAIN_PRODUCT_FORM}.is--main-sticky`);

  window.addEventListener(
    'click',
    (event) => {
      const stickyAdd = event.target.closest?.(`${STICKY_ATC} [data-action-atc]`);
      if (stickyAdd) {
        if (document.body.classList.contains('evano-pdp--editorial-focus')) {
          const request = new CustomEvent('evano:sticky-add-request', {
            cancelable: true,
            detail: { originalEvent: event }
          });
          if (!document.dispatchEvent(request)) return;
        }

        const sticky = stickyAdd.closest(STICKY_ATC);
        const form = mainProductForm();
        const missingSize = form && sizeGroups(form).some((group) => {
          const confirmedValue = confirmedSizeValue(form, group);
          return !confirmedValue || confirmedValue !== selectedSizeValue(group);
        });
        const shouldUseNativePrompt =
          missingSize &&
          window.matchMedia('(max-width: 767px)').matches &&
          stickyAdd.hasAttribute('data-action-delay') &&
          !hasVisibleStickyVariant(sticky);

        if (shouldUseNativePrompt) {
          clearError(form, sticky);
          scheduleNativeStickyPrompt(sticky);
          return;
        }

        guardAdd(event, form, sticky);
        return;
      }

      const form = event.target.closest?.(PROTECTED_FORM);
      if (form) {
        const addButton = event.target.closest?.('[data-atc-form]');
        if (addButton && form.contains(addButton)) {
          guardAdd(event, form);
          return;
        }

        const option = event.target.closest?.(OPTION_ITEM);
        if (option && form.contains(option)) {
          const group = option.closest(OPTION_GROUP);
          if (isSizeGroup(group)) {
            rememberConfirmedSize(form, group, option.dataset.value);
            if (requiresExplicitSize(form)) {
              presentConfirmedSize(group, option.dataset.value);
              window.setTimeout(() => {
                resolveExplicitVariant(form, group, option.dataset.value);
                scheduleReconcile(form);
              }, 0);
            }
            clearError(form, document.querySelector(STICKY_ATC));
          }
          scheduleReconcile(form);
          return;
        }
      }

      const stickyVariant = event.target.closest?.(
        `${STICKY_ATC} [data-dropdown-item]`
      );
      if (stickyVariant) {
        const mainForm = mainProductForm();
        if (!mainForm) return;
        clearError(mainForm, stickyVariant.closest(STICKY_ATC));
        scheduleConfirmEffectiveSizes(mainForm);
      }
    },
    true
  );

  window.addEventListener(
    'submit',
    (event) => {
      const form = event.target.closest?.(PROTECTED_FORM);
      if (form) guardAdd(event, form);
    },
    true
  );

  const initializeForms = (root = document, defer = false) => {
    const forms = new Set(root.querySelectorAll?.(PROTECTED_FORM) || []);
    const owningForm = root.closest?.(PROTECTED_FORM);
    if (owningForm) forms.add(owningForm);

    forms.forEach(defer ? scheduleReconcile : reconcileSizePresentation);
  };

  const reconcileMutatedSizeLabel = (mutation) => {
    const target = mutation.target.nodeType === Node.TEXT_NODE
      ? mutation.target.parentElement
      : mutation.target;
    const currentValue = target?.closest?.('[data-current-value]');
    const group = currentValue?.closest?.(OPTION_GROUP);
    const form = group?.closest?.(PROTECTED_FORM);
    if (!form || !isSizeGroup(group)) return;

    const selectedValue = selectedSizeValue(group);
    const confirmedValue = confirmedSizeValue(form, group);
    const expectedValue = confirmedValue && selectedValue === confirmedValue
      ? confirmedValue
      : SIZE_PROMPT;

    if (currentValue.textContent !== expectedValue) scheduleReconcile(form);
  };

  initializeForms();
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      reconcileMutatedSizeLabel(mutation);
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) initializeForms(node, true);
      });
    });
  }).observe(document.body, {
    characterData: true,
    childList: true,
    subtree: true
  });
})();
